<template>
  <form class="space-y-4" @submit.prevent="submit">
    <label
      class="input rounded-full bg-brand-bgdivider-20 input-bordered flex items-center gap-2 text-brand-secondary-80"
    >
      <Icon icon="mdi:magnify" class="text-xl" />
      <input
        v-model="search"
        type="text"
        class="grow placeholder:text-brand-secondary-80 text-base"
        :placeholder="$t('channel.communities.search.placeholder')"
      />
    </label>

    <ul class="space-y-2.5">
      <li
        v-for="community in filteredCommunities"
        :key="community.id"
        class="flex flex-row gap-2.5 justify-between items-center p-2.5 bg-brand-bgdivider-20 rounded-full"
      >
        <span class="gap-2.5 flex flex-row items-center">
          <img
            v-if="community.image"
            :src="community.image"
            class="size-8"
            :alt="community.name"
          />

          {{ community.name }}
        </span>

        <ButtonV2
          :variant="communityIdToSelected[community.id] ? 'subtle' : 'primary'"
          size="sm"
          class="!min-h-0 !py-1"
          type="button"
          @click="
            communityIdToSelected[community.id] =
              !communityIdToSelected[community.id]
          "
        >
          {{
            $t(
              `channel.communities.actions.${communityIdToSelected[community.id] ? "following" : "follow"}`,
            )
          }}
        </ButtonV2>
      </li>
    </ul>

    <Button
      variant="outlined"
      type="submit"
      class="w-full"
      :disabled="!hasSelectedAnyCommunity"
      :is-loading="isSubmitting"
    >
      <slot name="submit">{{ $t("channel.communities.actions.save") }}</slot>
    </Button>
  </form>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { type Community } from "@/generated/graphql";
import { Icon } from "@iconify/vue";
import Button from "@/components/ui/button/button.vue";
import {
  EventCategory,
  TrackEventEnum,
} from "@/services/tracking/tracking-types";
import { useTrackingStore } from "@/stores/tracking.store";
import { useChannelStore } from "@/stores/channel.store";
import { useUserStore } from "@/stores/user.store";
import ButtonV2 from "../ui/button/button-v2.vue";

const _userStore = useUserStore();
const _channelStore = useChannelStore();
const trackingService = useTrackingStore();

const emit = defineEmits<{
  submit: [
    communitiesToSelectIds: string[],
    communitiesToUnselectIds: string[],
  ];
}>();
const props = defineProps<{
  communities: Pick<Community, "id" | "name" | "image">[];
  selectedCommunitiesIds: string[];
  isSubmitting?: boolean;
}>();

const search = ref("");
const communityIdToSelected = ref<Record<string, boolean>>({});

const filteredCommunities = computed(() => {
  const result: Pick<Community, "name" | "id" | "image">[] = [];

  const communities = props.communities;

  if (communities) {
    for (const community of communities) {
      if (
        !community.name
          .toLocaleLowerCase()
          .includes(search.value.toLocaleLowerCase())
      ) {
        continue;
      }

      result.push(community);
    }
  }

  return result;
});

const hasSelectedAnyCommunity = computed(() =>
  Object.values(communityIdToSelected.value).some((isSelected) => isSelected),
);

watch(
  () => props.selectedCommunitiesIds,
  () => {
    communityIdToSelected.value = {};
    for (const communityId of props.selectedCommunitiesIds) {
      communityIdToSelected.value[communityId] = true;
    }
  },
  { immediate: true },
);

function submit() {
  const selectedList: string[] = [];
  const unselectedList: string[] = [];
  const allCommunities = props.communities ?? [];

  for (const community of allCommunities) {
    if (communityIdToSelected.value[community.id] === true) {
      selectedList.push(community.id);
    } else {
      unselectedList.push(community.id);
    }
  }

  trackingService.track({
    event: TrackEventEnum.CommunitySelected,
    category: EventCategory.Community,
    metadata: {
      selectedCommunityIds: selectedList,
      selectedCommunityNames: selectedList.map(
        (id) => allCommunities.find((community) => community.id === id)!.name,
      ),
    },
  });
  emit("submit", selectedList, unselectedList);
}
</script>
